"use client";

/**
 * Capture the page the user is on so we can send them back there after
 * sign-in. Most auth entry points used to hardcode a bare path (e.g.
 * `/auth/login?return_to=%2Fmap`), which dropped the query string - so a
 * visitor who opened `/map?layer=zones` and signed in landed on `/map`
 * with the default layer. These hooks build `return_to` from the LIVE
 * location instead, preserving `?layer=zones`, `?prefill=…`, etc.
 *
 * The login/register pages + the OAuth callback already honour the full
 * value (their `safeReturnTo` only checks the leading slash, keeping the
 * query) and re-add the locale, so a locale-less path is what we want.
 *
 * Deliberately uses `usePathname()` + `window.location` (in an effect)
 * rather than `useSearchParams()`: the latter would force every page that
 * renders the global header into client-only rendering (and break the
 * static SEO landings' build). `usePathname()` carries no such penalty;
 * we read the live query off `window.location` once mounted.
 */
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { locales } from "@/lib/i18n-config";

const LOCALE_RE = new RegExp(`^/(${locales.join("|")})(?=/|$)`);

/** Locale-less current path + query (empty string until mounted on the client). */
export function useCurrentReturnTo(): string {
  const pathname = usePathname();
  const [returnTo, setReturnTo] = useState("");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = window.location.pathname.replace(LOCALE_RE, "") || "/";
    setReturnTo(path + window.location.search);
    // pathname is the dep so this re-runs on client-side navigation; the
    // live query is read off window.location at that moment.
  }, [pathname]);
  return returnTo;
}

/**
 * `/auth/{login|register}?return_to=<current page>`. Falls back to the
 * bare auth path before hydration, or when already on an /auth route
 * (so we never bounce a user back into the sign-in flow).
 */
export function useAuthHref(target: "login" | "register" = "login"): string {
  const returnTo = useCurrentReturnTo();
  if (!returnTo || returnTo.startsWith("/auth")) return `/auth/${target}`;
  return `/auth/${target}?return_to=${encodeURIComponent(returnTo)}`;
}
